import { classNames } from '@shopify/css-utilities'
import { Image, ProductPrice, useProductOptions } from '@shopify/hydrogen'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import styles from '../../assets/scss/components/products/product-details.module.scss'
import { AcfMarkdown } from '../../lib/acf'
import { ProductMetafields } from '../../lib/types'
import { useI18n } from '../i18n.client'
import { ProductAccordion } from './product-accordion.client'
import { ProductButton } from './product-button.client'
import { ProductLayoutContext } from './product-layout-provider.client'
import { ProductOptions } from './product-options.client'

export const ProductDetails: React.FC<{
  descriptions: AcfMarkdown[]
  product: ProductMetafields
  titles: string[]
}> = ({ descriptions, product, titles }) => {
  const [i18n] = useI18n()
  const ref = useRef<HTMLDivElement>(null)
  const { setProductDetailsRef } = useContext(ProductLayoutContext)
  const [isBackgroundActive, setIsBackgroundActive] = useState(true)
  const { selectedVariant } = useProductOptions()

  useEffect(() => {
    setProductDetailsRef(ref)
  }, [ref])

  return (
    <div className={styles.stickyArea} ref={ref}>
      <div className={styles.sticky}>
        <div className={classNames(styles.container, isBackgroundActive && styles.active)}>
          <div className={styles.background}>
            <div className={styles.scrollContainer}>
              <div className={styles.header}>
                <h1 className={styles.title}>{product.title}</h1>
                <div className={classNames(styles.eyeIcon, !isBackgroundActive && styles.active)}>
                  <Image
                    alt={i18n.translate('toggle-background')}
                    height={38}
                    onClick={() => setIsBackgroundActive(!isBackgroundActive)}
                    src="/eye-hover-icon.svg"
                    width={50}
                  />
                </div>
              </div>
              {(!product.priceOnAsking || product.priceOnAsking.value === 'false') && (
                <div className={styles.priceContainer}>
                  <ProductPrice data={product} variantId={selectedVariant?.id} />
                  <ProductPrice
                    className={styles.discounted}
                    data={product}
                    priceType="compareAt"
                    variantId={selectedVariant?.id}
                  />
                </div>
              )}
              <div className={styles.description} dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
              <ProductPurchasing product={product} />
              <ProductAccordion descriptions={descriptions} product={product} titles={titles} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ProductPurchasing: FC<{ product: ProductMetafields }> = ({ product }) => {
  const [i18n] = useI18n()
  const { selectedVariant } = useProductOptions()
  const { isTouched } = useContext(ProductLayoutContext)

  if (!product.priceOnAsking || product.priceOnAsking.value === 'false') {
    return (
      <>
        <ProductOptions />
        <ProductButton className={styles.buyButton} selectedVariant={selectedVariant} />
      </>
    )
  }

  const variant = selectedVariant?.title !== 'Default Title' && isTouched ? ` – ${selectedVariant?.title}` : ''

  return (
    <>
      <ProductOptions />
      <a href={`mailto:hello@marielichtenberg.com?subject=${product.title}${variant}`} className={styles.buyButton}>
        {i18n.translate('poa')}
      </a>
    </>
  )
}
